import { ArrowRight } from "lucide-react";
import type { ReactNode } from "react";
import { Button } from "./Button";
import Image from "next/image";
import { resolveMediaUrl } from "@/lib/media";
import type { Settings } from "@/lib/dto";
import { cn } from "@/lib/format";

// Splits `title` on the first occurrence of `accent` and wraps it in a bold,
// brand-colored <b> — matches production's real per-title accent word
// (e.g. "Discounts" in "Deals & Discounts") without using dangerouslySetInnerHTML.
// Exported so components with their own heading markup (DepartureTrips) can
// reuse the same accenting logic instead of duplicating it.
export function renderAccentedTitle(title: string, accent?: string) {
  if (!accent) return title;
  const index = title.indexOf(accent);
  if (index === -1) return title;
  return (
    <>
      {title.slice(0, index)}
      <b className="text-primary">{accent}</b>
      {title.slice(index + accent.length)}
    </>
  );
}

type Props = {
  eyebrow?: string;
  title: string;
  /** Substring of `title` to render bold in the brand accent color —
   *  matches production's real per-title `<b>` word (e.g. "Discounts" in
   *  "Deals & Discounts"). Rendered as plain text elsewhere, not HTML. */
  accent?: string;
  lede?: string;
  align?: "left" | "center";
  cta?: { label: string; href: string };
  /** Slot rendered in the heading's trailing column — e.g. carousel
   *  navigation + pagination, alongside or in place of the CTA. */
  actions?: ReactNode;
  /** Avatar image URLs shown as a social-proof cluster (brand variant). */
  testimonialsAvatars?: string[];
  settings?: Partial<Settings> &
    Partial<{ tripadvisor_review_count: string | number }>;
  /** `default` retains the legacy layout; `brand` renders the resources
   *  `.section-head` pattern with mono eyebrow + display heading. */
  variant?: "default" | "brand";
  className?: string;
};

export function SectionHeading({
  eyebrow,
  title,
  accent,
  lede,
  align = "left",
  cta,
  actions,
  variant = "default",
  testimonialsAvatars,
  settings,
  className,
}: Props) {
  const titleNode = renderAccentedTitle(title, accent);

  // Normalize the optional review count (CMS may send a string or number) and
  // format with thousands separators. Falsy/zero/NaN → no count, so the
  // social-proof line never renders a bare "+".
  const rawCount = Number(settings?.tripadvisor_review_count);
  const reviewCount =
    Number.isFinite(rawCount) && rawCount > 0
      ? rawCount.toLocaleString("en-US")
      : null;

  if (variant === "brand") {
    const centered = align === "center";
    return (
      <div
        className={cn(
          "",
          centered
            ? "mb-6 md:mb-9 section-head"
            : "grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 md:gap-6 lg:gap-8 items-center mb-6 md:mb-9 section-head",
          className,
        )}
      >
        <div className={centered ? "flex flex-col items-center" : undefined}>
          {eyebrow ? (
            <span
              className={`subtitle text-[11px] tracking-[0.22em] uppercase flex items-center gap-2.5 font-semibold before:w-7 before:h-[1px] before:bg-current before:opacity-55 ${centered ? "after:w-7 after:h-[1px] after:bg-current after:opacity-55" : ""}`}
            >
              {eyebrow}
            </span>
          ) : null}
          {testimonialsAvatars && testimonialsAvatars.length > 0 ? (
            <div className="flex flex-wrap mt-3 mb-3.5 text-muted-soft items-center gap-2.5 text-sm">
              <div className="testimonials-avatars inline-flex justify-center [&>span+span]:-ml-3">
                {testimonialsAvatars.slice(0, 5).map((avatar, index) => (
                  <span
                    key={index}
                    className="avatar inline-block w-9 h-9 rounded-full overflow-hidden"
                  >
                    <Image
                      height={40}
                      width={40}
                      src={resolveMediaUrl(avatar) ?? avatar}
                      alt={`Testimonial Avatar ${index + 1}`}
                      className="w-9 h-9 rounded-full border-2 border-white shadow-sm object-cover"
                    />
                  </span>
                ))}
              </div>
              <span>
                {reviewCount ? (
                  <>
                    Over{" "}
                    <b className="text-headings">{reviewCount}+ travelers</b>{" "}
                    chose us last year 💚
                  </>
                ) : (
                  <>
                    Loved by travelers{" "}
                    <b className="text-headings">around the world</b> 💚
                  </>
                )}
              </span>
            </div>
          ) : null}
          <h2
            className={`section-title font-display text-[clamp(28px,4.2vw,44px)] leading-[1.2] font-extrabold text-balance${
              centered ? " text-center" : ""
            }`}
          >
            {titleNode}
          </h2>
          {lede ? (
            <p
              className={`lede text-[1.0675rem] leading-[1.55] text-muted max-w-[830px] mt-4${
                centered ? " text-center" : ""
              }`}
            >
              {lede}
            </p>
          ) : null}
        </div>
        {cta || actions ? (
          <div className="flex items-center gap-3">
            {cta ? (
              <Button
                arrow
                variant="outline"
                href={cta.href}
                className="uppercase tracking-wider text-xs font-bold"
              >
                {cta.label}
              </Button>
            ) : null}
            {actions}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className={
        align === "center"
          ? "text-center max-w-2xl mx-auto"
          : "flex flex-col md:flex-row md:items-end md:justify-between gap-4"
      }
    >
      <div className={align === "center" ? "" : "max-w-xl"}>
        {eyebrow ? <p className="eyebrow mb-2">{eyebrow}</p> : null}
        <h2 className="font-display font-black text-[clamp(1.5rem,3.5vw,1.5rem)] leading-tight">
          {titleNode}
        </h2>
        {lede ? (
          <p className="mt-3 text-[1.06275rem] text-muted">{lede}</p>
        ) : null}
      </div>
      {(cta || actions) && align !== "center" ? (
        <div className="flex items-center gap-4">
          {cta ? (
            <Button
              variant="link"
              href={cta.href}
              className="inline-flex items-center gap-1"
            >
              {cta.label}{" "}
              <ArrowRight
                size={14}
                aria-hidden
                className="rotate-45 inline-block"
              />
            </Button>
          ) : null}
          {actions}
        </div>
      ) : null}
    </div>
  );
}
